// SoundboardPlayerProvider — die Klangbuehne laeuft app-weit weiter.
//
// Julius (18.09.2026): „mach die Wiedergabe hartnaeckig, sodass ich als User
// etwas starten kann und es auch weiterlaeuft, wenn ich auf anderen Unterseiten
// unterwegs bin." Deshalb liegt der Player NICHT mehr auf der Soundboard-Seite
// (dort starb er beim Routenwechsel mit dem Unmount), sondern als Provider an
// der App-Wurzel: ein einziges HTMLAudioElement im LiveMusicPlayer, das keine
// Route je abbaut. Die Seite bedient nur noch diesen Provider, die Mini-Leiste
// (SoundboardMiniBar) zeigt und steuert ihn ueberall sonst.
//
// Wiederverwendet (NICHT veraendert):
//  - createLiveMusicPlayer (../components/chat/live/music.ts) — WS-freier
//    HTMLAudioElement-Player mit Fade/Loop/Ducking/Hintergrund-Tick.
//
// Geteilt (Besitzer-Vertrag, siehe ./nativeAudioSession.ts):
//  - acquireNativeAudioSession/release — Refcount um startNativeCallMode.
//    Das Hoerbuch, die Tischrunden-Aufnahme und das Live-Gespraech nutzen
//    dieselbe native Ressource; ohne Zaehler riss deren Stopp die
//    Hintergrund-Wiedergabe der Klangbuehne ab.
//
// REGEL „nur EINE laute Musikquelle" (Ducking-Vertrag, ./duckingContract.ts):
//  - Vorlesen (source 'tts') → Klangbuehne wird GEDUCKT (0,08 statt 0,25) und
//    kommt danach von selbst zurueck.
//  - Live-Gespraech/Anruf/Hoerbuch (source 'live') → die Sitzung bringt ihre
//    eigene Tonspur mit, also PAUSIERT die Klangbuehne komplett (echte Pause,
//    kein Stopp — sie nimmt danach an derselben Stelle wieder auf).
//
// iOS-REGEL: audio.play() MUSS in der Nutzergeste laufen. Deshalb starten
// play()/resume() den Player SYNCHRON im Klick; der Effekt unten gleicht den
// Zustand danach nur noch ab (LiveMusicPlayer.play ist fuer denselben
// laufenden Track idempotent). Ein play() erst im Passive-Effect waere ein
// neuer Task — WebKit lehnt es dort ab und die Leiste zeigte „laeuft" ohne Ton.
//
// Nach einem Neustart der App ist der zuletzt gewaehlte Titel gemerkt
// (localStorage), die Wiedergabe startet aber NICHT von allein — Autoplay ohne
// Nutzergeste lehnen iOS/Android ohnehin ab.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { getLiveApiBase } from '../components/chat/live/api';
import { createLiveMusicPlayer, type LiveMusicPlayer } from '../components/chat/live/music';
import { useT } from '../i18n';
import {
  onNativeEndRequested,
  onNativeInterruptionEnded,
} from '../native/liveCall';
import {
  DUCKING_END_EVENT,
  DUCKING_START_EVENT,
  duckingSourceOf,
} from './duckingContract';
import {
  NATIVE_AUDIO_RANK,
  acquireNativeAudioSession,
  releaseNativeAudioSession,
} from './nativeAudioSession';

const TRACK_PREF = 'lorath_soundboard_track';
const VOLUME_PREF = 'lorath_soundboard_volume';
const NATIVE_OWNER = 'soundboard';

// Bewusst NICHT clientPrefs (readPref/writePref): das schreibt immer auch ein
// Cookie mit path=/ und einem Jahr Laufzeit. Der Wiedergabezustand (Szenen-Id,
// Musik-URL, Titel) ginge damit bei JEDEM Request an app.lorath.eu mit —
// inklusive aller API- und Bild-Abrufe — obwohl der Server ihn nie liest. Der
// Cookie-Kanal von clientPrefs existiert nur, weil Theme/Schrift/Sprache mit
// den Legacy-HTML-Seiten geteilt werden; die Klangbuehne teilt nichts.
function readLocal(name: string): string | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage.getItem(name);
  } catch {
    return null; // Privatmodus/Quota darf nie werfen.
  }
}

function writeLocal(name: string, value: string): void {
  try {
    localStorage?.setItem(name, value);
  } catch {
    /* Privatmodus/Quota — der gemerkte Titel ist Komfort, kein Muss. */
  }
}

export interface SoundboardTrack {
  sceneId: number;
  url: string;
  title: string;
}

export interface SoundboardPlayerApi {
  /** Aktueller bzw. zuletzt gewaehlter Titel (ueberlebt den Neustart). */
  track: SoundboardTrack | null;
  /** Eine Wiedergabe laeuft (auch pausiert) — nur dann zeigt die Mini-Leiste. */
  active: boolean;
  /** Gerade hoerbar: aktiv, nicht pausiert, nicht von einem Gespraech verdraengt. */
  playing: boolean;
  /** Ein Live-Gespraech/Hoerbuch hat die Klangbuehne voruebergehend verdraengt. */
  suspended: boolean;
  /** Szene, die gerade laeuft ODER pausiert ist; null = keine. */
  playingSceneId: number | null;
  /** Nutzer-Lautstaerke 0..1 (multiplikativ auf die Musik-Grundlautstaerke). */
  volume: number;
  play: (url: string, title: string, sceneId: number) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  setVolume: (value: number) => void;
}

const SoundboardPlayerContext = createContext<SoundboardPlayerApi | null>(null);

function readStoredTrack(): SoundboardTrack | null {
  const raw = readLocal(TRACK_PREF);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const { sceneId, url, title } = parsed as Partial<SoundboardTrack>;
    if (typeof sceneId !== 'number' || typeof url !== 'string' || !url) return null;
    return { sceneId, url, title: typeof title === 'string' ? title : '' };
  } catch {
    return null;
  }
}

function readStoredVolume(): number {
  const stored = readLocal(VOLUME_PREF);
  if (stored === null || stored.trim() === '') return 1;
  const raw = Number(stored);
  return Number.isFinite(raw) && raw >= 0 && raw <= 1 ? raw : 1;
}

export function SoundboardPlayerProvider({ children }: { children: ReactNode }) {
  const { t } = useT();
  const [track, setTrack] = useState<SoundboardTrack | null>(readStoredTrack);
  const [active, setActive] = useState(false);
  const [paused, setPaused] = useState(false);
  const [volume, setVolumeState] = useState(readStoredVolume);
  // Zaehler statt Boolean: Vorlesen und Gespraech koennen sich ueberlappen, und
  // ein einzelnes 'tts:end' darf dann nicht den anderen Zustand aufheben. Der
  // Gegenvertrag (jedes start hat genau ein end) liegt in useTts/useAudiobook/
  // useLiveTalk; die Reset-Zeile unten ist nur das Sicherheitsnetz.
  const [duckDepth, setDuckDepth] = useState(0);
  const [suspendDepth, setSuspendDepth] = useState(0);

  const musicRef = useRef<LiveMusicPlayer | null>(null);
  const volumeRef = useRef(volume);
  volumeRef.current = volume;
  const duckedRef = useRef(duckDepth > 0);
  duckedRef.current = duckDepth > 0;
  const suspendedRef = useRef(suspendDepth > 0);
  suspendedRef.current = suspendDepth > 0;
  const trackRef = useRef(track);
  trackRef.current = track;
  // Die Notification-Texte sollen den Effekt NICHT neu ausloesen: ein
  // Sprachwechsel mitten in der Wiedergabe wuerde sonst den nativen Modus
  // abbauen und neu aufbauen (Notification blinkt, Audio-Fokus wackelt).
  const tRef = useRef(t);
  tRef.current = t;

  const playing = active && !paused && suspendDepth === 0;

  const ensurePlayer = useCallback((): LiveMusicPlayer => {
    let music = musicRef.current;
    if (!music) {
      music = createLiveMusicPlayer(getLiveApiBase);
      musicRef.current = music;
    }
    music.setVolume(volumeRef.current);
    music.setDucked(duckedRef.current);
    return music;
  }, []);

  const play = useCallback((url: string, title: string, sceneId: number) => {
    if (!url) return;
    const next: SoundboardTrack = { sceneId, url, title };
    // SYNCHRON in der Geste (iOS): Element entsperren + starten. Der Effekt
    // unten ruft play() gleich noch einmal — fuer denselben laufenden Track
    // ist das ein No-Op (music.ts: `active && currentUrl === url`).
    if (!suspendedRef.current) {
      const music = ensurePlayer();
      music.prime();
      music.play(next.url, next.title);
    }
    setTrack(next);
    setActive(true);
    setPaused(false);
    writeLocal(TRACK_PREF, JSON.stringify(next));
  }, [ensurePlayer]);

  const pause = useCallback(() => {
    musicRef.current?.pause();
    setPaused(true);
  }, []);

  const resume = useCallback(() => {
    // Ebenfalls synchron in der Geste — der Klick auf die Mini-Leiste muss den
    // play()-Aufruf tragen, nicht der Effekt.
    if (!suspendedRef.current) musicRef.current?.resume();
    setPaused(false);
  }, []);

  const stop = useCallback(() => {
    musicRef.current?.stop();
    setActive(false);
    setPaused(false);
  }, []);

  const setVolume = useCallback((value: number) => {
    const safe = Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 1;
    setVolumeState(safe);
    writeLocal(VOLUME_PREF, String(safe));
  }, []);

  // Aktionen als Refs: die Listener unten (native Notification, MediaSession)
  // werden einmal gebunden und muessen trotzdem immer den aktuellen Zustand
  // treffen.
  const stopRef = useRef(stop);
  stopRef.current = stop;
  const pauseRef = useRef(pause);
  pauseRef.current = pause;
  const resumeRef = useRef(resume);
  resumeRef.current = resume;

  // Titel/Aktivitaet → Player. BEWUSST ohne Cleanup: ein Track-Wechsel soll
  // den internen Ueberblend-Pfad des LiveMusicPlayer nutzen, kein stop()/play().
  // Waehrend ein Gespraech die Buehne besitzt, wird NICHT gestartet (sonst
  // liefe ein play() sofort in ein pause()); sobald es endet, laeuft der
  // Effekt erneut und holt den Start nach.
  const suspended = suspendDepth > 0;
  useEffect(() => {
    if (!active || !track) {
      musicRef.current?.stop();
      return;
    }
    if (suspended) return;
    ensurePlayer().play(track.url, track.title);
  }, [ensurePlayer, active, suspended, track]);

  // Hoerbarkeit → Player. ECHTE Pause statt stop(): stop() fadet aus und
  // vergisst den Track, das folgende play() laedt die Datei neu und beginnt
  // bei 0. Nach einem Live-Gespraech soll die Szene dort weiterlaufen, wo sie
  // verdraengt wurde („Weiter abspielen" muss das auch halten).
  useEffect(() => {
    const music = musicRef.current;
    if (!music || !active) return;
    if (playing) music.resume();
    else music.pause();
  }, [active, playing]);

  useEffect(() => {
    musicRef.current?.setVolume(volume);
  }, [volume]);

  useEffect(() => {
    musicRef.current?.setDucked(duckDepth > 0);
  }, [duckDepth]);

  // Ducking-Vertrag: 'tts' duckt, 'live' pausiert (siehe Kopf).
  useEffect(() => {
    const onStart = (event: Event) => {
      if (duckingSourceOf(event) === 'live') setSuspendDepth((depth) => depth + 1);
      else setDuckDepth((depth) => depth + 1);
    };
    const onEnd = (event: Event) => {
      if (duckingSourceOf(event) === 'live') setSuspendDepth((depth) => Math.max(0, depth - 1));
      else setDuckDepth((depth) => Math.max(0, depth - 1));
    };
    document.addEventListener(DUCKING_START_EVENT, onStart);
    document.addEventListener(DUCKING_END_EVENT, onEnd);
    return () => {
      document.removeEventListener(DUCKING_START_EVENT, onStart);
      document.removeEventListener(DUCKING_END_EVENT, onEnd);
    };
  }, []);

  // Sicherheitsnetz: ein verlorenes 'tts:end' (Fremd-Hoerer, Alt-Bundle) darf
  // die Klangbuehne nicht dauerhaft leise lassen. Sobald nichts mehr laeuft,
  // faellt der Duck-Zaehler auf 0 zurueck. Der Suspend-Zaehler bleibt bewusst
  // stehen — ein laufendes Gespraech besitzt die Stille weiterhin.
  useEffect(() => {
    if (!active) setDuckDepth(0);
  }, [active]);

  // Hintergrund-Wiedergabe: nativer Playback-Modus (Notification mit
  // Stopp-Knopf, Hintergrund-Audio). Gehalten wird er, solange der Nutzer die
  // Klangbuehne nicht selbst pausiert oder gestoppt hat — auch waehrend ein
  // Gespraech sie verdraengt. Der Refcount sorgt dafuer, dass das Gespraech
  // die Notification bekommt (hoeherer Rang) und ihr Ende hier nichts abreisst.
  // KEIN Wake-Lock: Hintergrundmusik ist gerade der Fall, in dem das Geraet
  // weggelegt wird — ein dauerhaft heller Bildschirm waere ein Akkufresser.
  const wantsNativeSession = active && !paused;
  useEffect(() => {
    if (!wantsNativeSession) return undefined;
    const translate = tRef.current;
    acquireNativeAudioSession(NATIVE_OWNER, {
      title: translate('soundboard.call_notification_title'),
      text: translate('soundboard.call_notification_text'),
      channelName: translate('soundboard.call_notification_channel'),
      endLabel: translate('soundboard.call_notification_end'),
      mode: 'playback',
    }, NATIVE_AUDIO_RANK.soundboard);
    const endHandle = onNativeEndRequested(() => stopRef.current());
    // iOS: AVAudioSession-Interruption vorbei (Anruf/Siri/Wecker) → WebKit
    // resumed pausierte Elemente nicht zuverlaessig selbst. pause()+resume()
    // nimmt an derselben Stelle wieder auf (play() wuerde neu laden).
    const interruptionHandle = onNativeInterruptionEnded(() => {
      const music = musicRef.current;
      if (!music || !trackRef.current || suspendedRef.current) return;
      music.pause();
      music.resume();
    });
    return () => {
      releaseNativeAudioSession(NATIVE_OWNER);
      try {
        endHandle.remove();
        interruptionHandle.remove();
      } catch {
        /* egal */
      }
    };
  }, [wantsNativeSession]);

  // MediaSession: Titel auf dem Sperrbildschirm + Kopfhoerer-/Systemtasten.
  // NUR aufraeumen, wenn wir sie selbst gesetzt haben — das Hoerbuch setzt
  // dieselben globalen Handler, und ein blindes Nullen beim Mount (active
  // false) loeschte dessen Sperrbildschirm-Knoepfe.
  const ownsMediaSessionRef = useRef(false);
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    const session = navigator.mediaSession;
    if (!active || !track) {
      if (!ownsMediaSessionRef.current) return;
      ownsMediaSessionRef.current = false;
      try {
        session.metadata = null;
        session.playbackState = 'none';
        session.setActionHandler('play', null);
        session.setActionHandler('pause', null);
        session.setActionHandler('stop', null);
      } catch {
        /* egal */
      }
      return;
    }
    try {
      if (typeof MediaMetadata !== 'undefined') {
        session.metadata = new MediaMetadata({
          title: track.title || t('soundboard.title'),
          artist: t('soundboard.title'),
        });
      }
      session.playbackState = playing ? 'playing' : 'paused';
      session.setActionHandler('play', () => resumeRef.current());
      session.setActionHandler('pause', () => pauseRef.current());
      session.setActionHandler('stop', () => stopRef.current());
      ownsMediaSessionRef.current = true;
    } catch {
      /* egal — nicht jede Engine kennt jede Aktion */
    }
  }, [active, playing, t, track]);

  // Nur beim echten Abbau der App-Wurzel — kein Routenwechsel kommt hier an.
  useEffect(() => {
    return () => {
      releaseNativeAudioSession(NATIVE_OWNER);
      if (ownsMediaSessionRef.current && typeof navigator !== 'undefined' && 'mediaSession' in navigator) {
        ownsMediaSessionRef.current = false;
        try {
          navigator.mediaSession.metadata = null;
          navigator.mediaSession.playbackState = 'none';
          navigator.mediaSession.setActionHandler('play', null);
          navigator.mediaSession.setActionHandler('pause', null);
          navigator.mediaSession.setActionHandler('stop', null);
        } catch {
          /* egal */
        }
      }
      const music = musicRef.current;
      musicRef.current = null;
      if (music) {
        try {
          music.dispose();
        } catch {
          /* egal */
        }
      }
    };
  }, []);

  const value = useMemo<SoundboardPlayerApi>(() => ({
    track,
    active,
    playing,
    suspended,
    playingSceneId: active && track ? track.sceneId : null,
    volume,
    play,
    pause,
    resume,
    stop,
    setVolume,
  }), [active, pause, play, playing, resume, setVolume, stop, suspended, track, volume]);

  return (
    <SoundboardPlayerContext.Provider value={value}>{children}</SoundboardPlayerContext.Provider>
  );
}

export function useSoundboardPlayer(): SoundboardPlayerApi {
  const value = useContext(SoundboardPlayerContext);
  if (!value) throw new Error('useSoundboardPlayer must be used within SoundboardPlayerProvider');
  return value;
}

/**
 * Nicht-werfende Variante (Muster von useOptionalT): fuer Bauteile, die in der
 * Shell haengen und auch ohne Provider mounten koennen — die Mini-Leiste
 * rendert dann schlicht nichts.
 */
export function useOptionalSoundboardPlayer(): SoundboardPlayerApi | null {
  return useContext(SoundboardPlayerContext);
}
